import useIntersectionObserver from "@react-hook/intersection-observer";
import { useEffect, useState, memo } from "react";
import HotspotGridItemHeader from "./HotspotGridItemHeader";
import { WallCardImageLinearGradient } from "../../../constants/gradientDefinitions";
import HotspotInfo from "./HotspotInfo";
import useWallStore from "../../../store/useWallStore";
import { vibrateDevice, VIBRATION_TYPE } from "../../../utils/addVibration";
import { useNavigate } from "react-router-dom";
import { trackEvent } from "../../../utils/mixpanel";

const HotspotGridItem = memo(({ hotspot, gridId, index }) => {
  const expandedHotspot = useWallStore((state) => state.expandedHotspot);

  const [target, setTarget] = useState(null);
  const [hasBeenVisible, setHasBeenVisible] = useState(false);

  // Observer for lazy loading (pre-loads items just outside the viewport)
  const { isIntersecting: isWithinLoadRange } = useIntersectionObserver(target, {
    threshold: 0,
    rootMargin: "250px", // Increased for even better pre-loading
  });

  // Observer for visual focus (determines when to remove grayscale)
  const { isIntersecting: isFocused } = useIntersectionObserver(target, {
    threshold: 0.5,
  });

  const isCenter = gridId === 5 && index === 4; // TODO: Handle scroll to view logic using store

  const navigate = useNavigate();

  const handleCardClick = () => {
    if (!isFocused) {
      return;
    }

    trackEvent("wall_hotspot_card_clicked", {
      hotspot_id: hotspot.originalId,
      hotspot_name: hotspot.title,
      position_index: index,
      experience_type: hotspot.experience,
    });

    // Open Hotspot Detail Page
    navigate(`/hotspot/${hotspot.id}`);
    // vibrateDevice(VIBRATION_TYPE.CLICK_HOTSPOT);
    return;
  };

  const isExpandedHotspotView = Boolean(expandedHotspot);
  const isActiveHotspot =
    expandedHotspot?.id === hotspot.id && expandedHotspot?.gridId === gridId;

  // Track when hotspot becomes intersecting (visible)
  useEffect(() => {
    if (isWithinLoadRange) {
      if (!hasBeenVisible) {
        setHasBeenVisible(true);
      }
      trackEvent("wall_hotspot_card_visible", {
        hotspot_id: hotspot.originalId,
        hotspot_name: hotspot.title,
        position_index: index,
      });
      vibrateDevice(VIBRATION_TYPE.SCROLL_HOTSPOTS);
    }
  }, [isWithinLoadRange, hotspot.originalId, hotspot.title, index, hasBeenVisible]);

  return (
    <div
      className={`hotspot-grid-item ${isActiveHotspot ? "active" : ""} ${
        isFocused ? "intersecting" : ""
      }`}
      style={{
        backgroundImage: hasBeenVisible
          ? `${WallCardImageLinearGradient}, url("${hotspot.bannerImgUrl}")`
          : "none",
        backgroundColor: "#1a1a1a", // Placeholder color
        borderRadius: "8px",
        minHeight: "150px", // Ensure it has a size even if empty
      }}
      ref={setTarget}
      onClick={isExpandedHotspotView ? null : handleCardClick}
      id={isCenter ? "center-hotspot-item" : ""}
    >
      {hasBeenVisible && (
        <>
          <HotspotGridItemHeader
            type={hotspot.experience}
            hostName={hotspot.host.name}
            hostPic={hotspot.host.profileImageUrl}
            invitees={hotspot.inviteeCount}
          />
          <HotspotInfo
            title={hotspot.title}
            venue={hotspot.venue}
            date={hotspot.date}
            titleFont={hotspot.font}
          />
        </>
      )}
    </div>
  );
});

export default HotspotGridItem;
